import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { CheckCircle, Clock, X, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, User } from 'lucide-react';

const AdminValidations = ({ user, activeModule = "b2b" }) => {
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchValidations();
  }, []);

  const fetchValidations = async () => {
    try {
      const response = await axios.get(`${API}/validations`, { withCredentials: true });
      setValidations(response.data);
    } catch (error) {
      console.error('Failed to fetch validations:', error);
      if (error.response && error.response.status < 500) toast.error("Liste des validations non chargée : vérifiez la connexion puis actualisez.");
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (scheduleId, action, reason = null) => {
    setProcessing(scheduleId);
    try {
      await axios.post(
        `${API}/schedule/${scheduleId}/validate`,
        { action: action, reason: reason },
        { withCredentials: true }
      );
      
      setProcessing(null);
      await fetchValidations();
      
      const messages = {
        'approve': '✅ Mission validée !',
        'reject': '❌ Mission refusée définitivement !',
        'request_correction': '⚠️ Demande de correction envoyée !'
      };
      setMessage(messages[action] || '✅ Action effectuée !');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error(`Failed to ${action} validation:`, error);
      setProcessing(null);
      setMessage('❌ Erreur lors de la validation: ' + (error.response?.data?.detail || 'Erreur inconnue'));
      fetchValidations();
      setTimeout(() => setMessage(''), 5000);
    }
  };

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Chivo']">Validations en Attente</h1>
          <p className="text-gray-500 mt-1">{validations.length} validation(s) en attente</p>
        </div>

        {validations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aucune validation en attente</p>
              <p className="text-gray-400 text-sm mt-2">Toutes les missions ont été traitées</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {validations.map((validation) => (
              <Card key={validation.schedule_id} className="border-yellow-200 bg-yellow-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-yellow-700">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <span>Validation en attente</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Employee Info */}
                    <div className="flex items-start gap-4 p-4 bg-white rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-[#0F172A]">{validation.user_name}</p>
                        <p className="text-sm text-gray-600">{validation.user_email}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">
                            {new Date(validation.date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        {validation.loading_time && (
                          <p className="text-sm text-gray-500 mt-1">
                            Chargement: {validation.loading_time}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Request Info */}
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Demande effectuée le:</p>
                      <p className="font-medium text-gray-900">
                        {new Date(validation.validation_requested_at).toLocaleString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {validation.comment && (
                        <>
                          <p className="text-sm text-gray-600 mt-3 mb-1">Commentaire:</p>
                          <p className="text-gray-900">{validation.comment}</p>
                        </>
                      )}
                      {validation.vehicle_plate && (
                        <p className="text-sm text-gray-700 mt-1"><strong>Plaque véhicule:</strong> {validation.vehicle_plate}</p>
                      )}
                      {(validation.start_time || validation.end_time) && (
                        <p className="text-sm text-gray-700 mt-1"><strong>Horaire:</strong> {validation.start_time || '-'} → {validation.end_time || '-'}</p>
                      )}
                      {validation.assistance_comment && (
                        <p className="text-sm text-gray-700 mt-2" data-testid={`admin-validation-assistance-comment-${validation.schedule_id}`}>
                          <strong>Assistance:</strong> {validation.assistance_comment}
                        </p>
                      )}
                      {(validation.backup_points_delivered !== undefined && validation.backup_points_delivered !== null) && (
                        <p className="text-sm text-gray-700 mt-1" data-testid={`admin-validation-backup-points-${validation.schedule_id}`}>
                          <strong>Backup points livrés:</strong> {validation.backup_points_delivered}
                        </p>
                      )}
                      {(validation.backup_returns !== undefined && validation.backup_returns !== null) && (
                        <p className="text-sm text-gray-700 mt-1" data-testid={`admin-validation-backup-returns-${validation.schedule_id}`}>
                          <strong>Backup retours:</strong> {validation.backup_returns}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-3">
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleValidation(validation.schedule_id, 'approve')}
                          disabled={processing === validation.schedule_id}
                          data-testid={`validation-approve-${validation.schedule_id}`}
                          className="flex-1 bg-green-500 hover:bg-green-600"
                        >
                          {processing === validation.schedule_id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Valider
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            const reason = prompt('Raison de la demande de correction:');
                            if (reason) {
                              handleValidation(validation.schedule_id, 'request_correction', reason);
                            }
                          }}
                          disabled={processing === validation.schedule_id}
                          data-testid={`validation-correction-${validation.schedule_id}`}
                          variant="outline"
                          className="flex-1 border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Correction
                        </Button>
                        <Button
                          onClick={() => {
                            if (window.confirm('Refuser définitivement cette mission ? Cette action est irréversible.')) {
                              const reason = prompt('Raison du refus (optionnel):');
                              handleValidation(validation.schedule_id, 'reject', reason);
                            }
                          }}
                          disabled={processing === validation.schedule_id}
                          data-testid={`validation-reject-${validation.schedule_id}`}
                          variant="outline"
                          className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {message && (
        <div className="fixed top-4 right-4 z-50 bg-white border-2 border-gray-300 rounded-lg shadow-lg p-4" data-testid="validation-result-message" role="status">
          <p className="text-sm font-medium">{message}</p>
        </div>
      )}
    </Layout>
  );
};

export default AdminValidations;
